/**
 * useNotifications Hook
 * React Hook for managing notifications state and operations
 */

import { useState, useEffect, useCallback } from 'react';
import notificationSystemService, {
  Notification,
  NotificationStatistics,
} from '../services/notification-system.service';

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  statistics: NotificationStatistics | null;
  loading: boolean;
  error: string | null;

  // Actions
  loadNotifications: (options?: { limit?: number; unreadOnly?: boolean }) => Promise<void>;
  loadUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [statistics, setStatistics] = useState<NotificationStatistics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load notifications
   */
  const loadNotifications = useCallback(async (options?: { limit?: number; unreadOnly?: boolean }) => {
    try {
      setLoading(true);
      setError(null);

      const result = await notificationSystemService.getNotifications({
        limit: options?.limit || 50,
        unreadOnly: options?.unreadOnly || false,
      });

      setNotifications(result.notifications);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load unread count
   */
  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await notificationSystemService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  }, []);

  /**
   * Load statistics
   */
  const loadStatistics = useCallback(async () => {
    try {
      const stats = await notificationSystemService.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationSystemService.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );

      // Refresh count
      await loadUnreadCount();
    } catch (err) {
      console.error('Failed to mark as read:', err);
      setError(err instanceof Error ? err.message : 'Failed to mark as read');
    }
  }, [loadUnreadCount]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationSystemService.markAllAsRead();

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );

      // Reset count
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      setError(err instanceof Error ? err.message : 'Failed to mark all as read');
    }
  }, []);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationSystemService.deleteNotification(notificationId);

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Refresh count
      await loadUnreadCount();
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete notification');
    }
  }, [loadUnreadCount]);

  /**
   * Refresh all data
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      loadNotifications(),
      loadUnreadCount(),
      loadStatistics(),
    ]);
  }, [loadNotifications, loadUnreadCount, loadStatistics]);

  /**
   * Initial load and polling
   */
  useEffect(() => {
    // Initial load
    refresh();

    // Poll unread count every 30 seconds
    const countInterval = setInterval(loadUnreadCount, 30000);

    // Poll notifications every 60 seconds
    const notificationsInterval = setInterval(() => {
      loadNotifications({ limit: 20 });
    }, 60000);

    return () => {
      clearInterval(countInterval);
      clearInterval(notificationsInterval);
    };
  }, [refresh, loadUnreadCount, loadNotifications]);

  return {
    notifications,
    unreadCount,
    statistics,
    loading,
    error,
    loadNotifications,
    loadUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
  };
}

export default useNotifications;
